import * as ExcelJS from 'exceljs';
import type { PayoutPaymentBatch } from '@/services/payoutPaymentService';

export type PaymentBatchStatusFilter = 'all' | 'active' | 'voided' | 'legacy';
export type PaymentBatchSortField = 'paymentDate' | 'checkNumber' | 'recipientName' | 'totalAmount';
export type PaymentBatchSortDirection = 'asc' | 'desc';

export function filterAndSortPaymentBatches(
  batches: PayoutPaymentBatch[],
  search: string,
  status: PaymentBatchStatusFilter,
  sortField: PaymentBatchSortField,
  sortDirection: PaymentBatchSortDirection
): PayoutPaymentBatch[] {
  const term = search.trim().toLowerCase();
  const filtered = batches.filter((batch) => {
    if (status === 'active' && batch.voidedAt) {
      return false;
    }
    if (status === 'voided' && !batch.voidedAt) {
      return false;
    }
    if (status === 'legacy' && batch.source !== 'legacy_backfill') {
      return false;
    }
    if (!term) {
      return true;
    }

    return [
      batch.checkNumber,
      batch.recipientName,
      batch.paymentDate,
      batch.memo || '',
      ...batch.allocations.flatMap((allocation) => [
        allocation.loanNumber,
        allocation.borrowerName || ''
      ])
    ].some((value) => value.toLowerCase().includes(term));
  });

  return [...filtered].sort((a, b) => {
    const left = a[sortField];
    const right = b[sortField];
    const comparison = typeof left === 'number' && typeof right === 'number'
      ? left - right
      : String(left).localeCompare(String(right), undefined, { numeric: true, sensitivity: 'base' });
    return sortDirection === 'asc' ? comparison : -comparison;
  });
}

export async function downloadPayoutPaymentLedger(
  batches: PayoutPaymentBatch[],
  filename = 'payout-payment-ledger.xlsx'
): Promise<void> {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'LendWise';
  workbook.created = new Date();

  const sheet = workbook.addWorksheet('Payment Ledger');
  sheet.columns = [
    { header: 'Check #', key: 'checkNumber', width: 18 },
    { header: 'Payment Date', key: 'paymentDate', width: 14 },
    { header: 'Payee', key: 'recipient', width: 24 },
    { header: 'Check Total', key: 'checkTotal', width: 16 },
    { header: 'Loan #', key: 'loanNumber', width: 18 },
    { header: 'Borrower', key: 'borrower', width: 24 },
    { header: 'Payout Type', key: 'payoutType', width: 14 },
    { header: 'Allocation', key: 'allocation', width: 16 },
    { header: 'Status', key: 'status', width: 12 },
    { header: 'Source', key: 'source', width: 18 },
    { header: 'History Complete', key: 'historyComplete', width: 18 },
    { header: 'Recorded By', key: 'createdBy', width: 22 },
    { header: 'Recorded At', key: 'createdAt', width: 22 },
    { header: 'Void Reason', key: 'voidReason', width: 32 },
    { header: 'Voided By', key: 'voidedBy', width: 22 },
    { header: 'Voided At', key: 'voidedAt', width: 22 },
    { header: 'Memo', key: 'memo', width: 32 }
  ];

  for (const batch of batches) {
    for (const allocation of batch.allocations) {
      sheet.addRow({
        checkNumber: batch.checkNumber,
        paymentDate: batch.paymentDate,
        recipient: batch.recipientName,
        checkTotal: batch.totalAmount,
        loanNumber: allocation.loanNumber,
        borrower: allocation.borrowerName || '',
        payoutType: allocation.payoutType,
        allocation: allocation.amount,
        status: batch.voidedAt ? 'Voided' : 'Active',
        source: batch.source,
        historyComplete: batch.historyComplete ? 'Yes' : 'No - legacy import',
        createdBy: batch.createdByName || '',
        createdAt: batch.createdAt,
        voidReason: batch.voidReason || '',
        voidedBy: batch.voidedByName || '',
        voidedAt: batch.voidedAt || '',
        memo: batch.memo || ''
      });
    }
  }

  const header = sheet.getRow(1);
  header.font = { bold: true };
  header.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8E8E8' } };
  sheet.views = [{ state: 'frozen', ySplit: 1 }];
  sheet.autoFilter = { from: 'A1', to: 'Q1' };
  sheet.getColumn('checkTotal').numFmt = '$#,##0.00;[Red]-$#,##0.00';
  sheet.getColumn('allocation').numFmt = '$#,##0.00;[Red]-$#,##0.00';

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}
